import React from "react";
import { useAgreementStatus } from "@/hooks/useAgreementStatus";
import LegalAgreementModal from "@/components/LegalAgreementModal";

interface Props {
  slug: string;
  children: React.ReactNode;
}

/**
 * Wraps any content behind a legal agreement acceptance check.
 * If the user has not accepted (or must re-accept), a full-screen blocking modal is shown.
 * Once accepted, the children are rendered normally.
 *
 * Usage:
 *   <AgreementGate slug="deployment-compliance">
 *     <MyPanel />
 *   </AgreementGate>
 */
export default function AgreementGate({ slug, children }: Props) {
  const { needsAcceptance, isLoading, agreement, refetch } =
    useAgreementStatus(slug);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (needsAcceptance && agreement) {
    return <LegalAgreementModal agreement={agreement} onAccepted={refetch} />;
  }

  // needsAcceptance stays true (useAgreementStatus's own fail-closed default) when the
  // status check itself failed (non-ok response or network error) — `agreement` is null
  // in that case because there's no agreement content to show yet, NOT because acceptance
  // isn't required. Rendering `children` here would fail OPEN on a fetch failure (an
  // outage, a blip, an offline admin) and let a user straight past a legally-required
  // acceptance gate. Block and offer a retry instead — never fall through to `children`
  // while `needsAcceptance` is true, agreement content or not.
  if (needsAcceptance && !agreement) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16 text-center px-6">
        <p className="text-sm text-zinc-600 max-w-sm">
          We couldn&rsquo;t verify your agreement acceptance status. This
          content is blocked until we can confirm it.
        </p>
        <button
          type="button"
          onClick={refetch}
          className="text-sm font-semibold text-blue-600 hover:text-blue-700 cursor-pointer"
        >
          Try again
        </button>
      </div>
    );
  }

  // Agreement is accepted.
  return <>{children}</>;
}
